import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { FormComponent, UiComponent } from 'components';
import {
  getAttendanceStatus,
  getSelectionStatus,
} from 'components/event/Detail/useVolunteerList';
import { usePageOption } from 'hooks';
import { EnrollQuery } from 'queries';
import { EnrollApi } from 'api';
import {
  ATTENDANCE_OPTIONS,
  SELECTION_OPTIONS,
  toStatusParam,
} from 'data/enroll';
import { DEFAULT_ERROR_MESSAGE } from 'data/message';
import {
  AttendanceStatusType,
  IEnroll,
  IEvent,
  IUserGroupCandidate,
  SelectionStatusType,
} from 'types';
import EventSelect from './EventSelect';
import { formatNameWithPhone } from './format';
import * as PickerStyle from './style';

const toCandidate = ({ user }: IEnroll): IUserGroupCandidate => ({
  id: user.id,
  name: user.name,
  phone: user.phone,
});

interface Props {
  selectedIds: Set<number>;
  onAdd: (users: IUserGroupCandidate[]) => void;
}

function EnrollSearch({
  selectedIds,
  onAdd,
  initialEvent,
  isEventLocked = false,
}: Props & { initialEvent?: IEvent; isEventLocked?: boolean }) {
  const [event, setEvent] = useState<IEvent | null>(initialEvent ?? null);
  const isLottery = event?.recruitmentType === 'LOTTERY';

  if (!event) return <EventSelect onSelect={setEvent} />;

  return (
    <>
      <div css={PickerStyle.selectedEvent}>
        <span>
          <strong>{event.title}</strong>
        </span>
        {!isEventLocked && (
          <UiComponent.Button
            variant="border"
            color="primary"
            onClick={() => setEvent(null)}
          >
            봉사 변경
          </UiComponent.Button>
        )}
      </div>
      <EnrollList
        key={event.id}
        eventId={event.id}
        isLottery={isLottery}
        selectedIds={selectedIds}
        onAdd={onAdd}
      />
    </>
  );
}

interface EnrollListProps extends Props {
  eventId: number;
  isLottery: boolean;
}

function EnrollList({
  eventId,
  isLottery,
  selectedIds,
  onAdd,
}: EnrollListProps) {
  const { pageOption, handlePage } = usePageOption(10);
  const [attendance, setAttendance] = useState<AttendanceStatusType[]>(
    ATTENDANCE_OPTIONS.map((o) => o.value),
  );
  const [selection, setSelection] = useState<SelectionStatusType[]>(
    SELECTION_OPTIONS.map((o) => o.value),
  );
  const [isFetchingAll, setIsFetchingAll] = useState(false);

  const statusQuery = {
    eventId,
    attendanceStatus: toStatusParam(attendance, ATTENDANCE_OPTIONS.length),
    selectionStatus: isLottery
      ? toStatusParam(selection, SELECTION_OPTIONS.length)
      : undefined,
  };

  const { data } = EnrollQuery.useGetAll({
    page: pageOption.page,
    size: pageOption.size,
    ...statusQuery,
  });
  const total: number = data?.total ?? 0;

  useEffect(() => {
    handlePage((prev) => ({ ...prev, page: 1 }));
  }, [attendance, selection]);

  const handleAddAll = async () => {
    if (isFetchingAll || total === 0) return;

    setIsFetchingAll(true);
    try {
      const res = await EnrollApi.getAll({
        page: 1,
        size: total,
        ...statusQuery,
      });
      onAdd((res.data.data as IEnroll[]).map(toCandidate));
    } catch {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsFetchingAll(false);
    }
  };

  const cols = useMemo(
    () => ['이름', ...(isLottery ? ['선정'] : []), '출결', '선택'],
    [isLottery],
  );

  const renderer: Record<
    string,
    (data: IEnroll & { row: number }) => ReactNode
  > = useMemo(
    () => ({
      이름: ({ user }: IEnroll) => (
        <>{formatNameWithPhone(user.name, user.phone)}</>
      ),
      선정: ({ selectionStatus }: IEnroll) => (
        <>{getSelectionStatus(selectionStatus)}</>
      ),
      출결: ({ attendanceStatus }: IEnroll) => (
        <>{getAttendanceStatus(attendanceStatus)}</>
      ),
      선택: (enroll: IEnroll) => {
        const isSelected = selectedIds.has(enroll.user.id);
        return (
          <UiComponent.Button
            variant={isSelected ? 'border' : 'contained'}
            color="primary"
            disabled={isSelected}
            onClick={() => onAdd([toCandidate(enroll)])}
          >
            {isSelected ? '선택됨' : '선택'}
          </UiComponent.Button>
        );
      },
    }),
    [selectedIds, onAdd],
  );

  const rows = (data?.data || []).map((e: IEnroll) => ({ ...e, rowKey: e.id }));

  return (
    <>
      <div css={PickerStyle.searchRow}>
        <FormComponent.MultiSelect
          title="출결 상태"
          options={ATTENDANCE_OPTIONS}
          value={attendance}
          handleChange={setAttendance}
        />
        {isLottery && (
          <FormComponent.MultiSelect
            title="선정 상태"
            options={SELECTION_OPTIONS}
            value={selection}
            handleChange={setSelection}
          />
        )}
      </div>
      <div css={PickerStyle.listWrap}>
        <UiComponent.Table
          cols={cols}
          renderer={renderer}
          data={rows}
          sticky
          empty="조건에 맞는 참여자가 없습니다"
        />
      </div>
      <UiComponent.Pagination
        pageOption={pageOption}
        handlePage={handlePage}
        totalPage={data?.totalPages || 1}
      />
      <div css={PickerStyle.bulkRow}>
        <UiComponent.Button
          variant="contained"
          color="primary"
          disabled={isFetchingAll || total === 0}
          onClick={handleAddAll}
        >
          {`조회된 ${total.toLocaleString('ko')}명 전체 선택`}
        </UiComponent.Button>
      </div>
    </>
  );
}

export default EnrollSearch;
